import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons'

import { Button, Flex, HStack, Input, Modal, Switch, T, VStack } from 'src/govwell-ui'
import useDisclosure from 'src/hooks/use-disclosure'
import { KeyboardKey } from 'src/types'
import {
  BACKEND_GATE_VALUES,
  BackendFeatureGate,
} from 'src/utils/featureFlagging/backendFeatureGates'
import { FeatureGate, useCheckStatsigGate } from 'src/utils/featureFlagging/featureFlagging'
import {
  areFeatureFlagOverridesEnabled,
  clearFeatureFlagOverrides,
  setFeatureFlagOverride,
  useFeatureFlagOverrides,
} from 'src/utils/featureFlagging/featureFlagOverrides'

// Where a gate is consumed. Derived by comparing the FE `FeatureGate` enum with
// the BE mirror (`BackendFeatureGate`); gates in both are `Both`, gates only in
// the BE mirror are surfaced as `Backend` rows the FE enum doesn't know about.
type FeatureGateScope = 'Frontend' | 'Backend' | 'Both'
type FeatureGateEntry = { label: string; gate: string; scope: FeatureGateScope }

const SCOPE_BADGE_CLASSES: Record<FeatureGateScope, string> = {
  Frontend: 'border-blue-300 bg-blue-100 text-blue-700',
  Backend: 'border-green-300 bg-green-100 text-green-700',
  Both: 'border-purple-300 bg-purple-100 text-purple-700',
}

const FrontendGateValues = new Set<string>(Object.values(FeatureGate))

const FeatureGateEntries: FeatureGateEntry[] = [
  ...Object.entries(FeatureGate).map(([label, gate]) => ({
    label,
    gate,
    scope: (BACKEND_GATE_VALUES.has(gate) ? 'Both' : 'Frontend') as FeatureGateScope,
  })),
  ...Object.entries(BackendFeatureGate)
    .filter(([, gate]) => !FrontendGateValues.has(gate))
    .map(([label, gate]) => ({ label, gate, scope: 'Backend' as FeatureGateScope })),
].sort((a, b) => a.label.localeCompare(b.label))

type FeatureFlagRowProps = {
  gate: string
  name: string
  override: boolean | undefined
  scope: FeatureGateScope
  statsigValue: boolean
}
const FeatureFlagRow = ({ gate, name, override, scope, statsigValue }: FeatureFlagRowProps) => {
  // The switch reflects the resolved value (override if set, else Statsig).
  const effectiveValue = override ?? statsigValue
  const isDeviating = effectiveValue !== statsigValue

  const handleValueChange = useCallback(
    (next: boolean) => {
      // Toggling back to the Statsig value clears the override (follow default);
      // any other value pins a local override.
      setFeatureFlagOverride(gate, next === statsigValue ? null : next)
    },
    [gate, statsigValue]
  )

  return (
    <Flex
      justify="space-between"
      align="center"
      gap={4}
      className={`rounded-md px-2 py-1 ${isDeviating ? 'bg-orange-50' : ''}`}
    >
      <VStack gap={0} minWidth={0}>
        <Flex gap={2} align="center">
          <T.Body className="font-medium" maxLines={1}>
            {name}
          </T.Body>
          <span
            className={`rounded-full border px-1.5 text-[11px] font-medium whitespace-nowrap ${SCOPE_BADGE_CLASSES[scope]}`}
          >
            {scope}
          </span>
          {isDeviating && (
            <span className="rounded-full border border-orange-300 bg-orange-100 px-1.5 text-[11px] font-medium whitespace-nowrap text-orange-700">
              Overridden
            </span>
          )}
        </Flex>
        <T.BodySmall className="text-neutral-500" maxLines={1}>
          {gate}
        </T.BodySmall>
      </VStack>
      <VStack gap={1} align="end" width="120px" minWidth="120px">
        <HStack gap={2} align="center">
          <T.Body
            className={`w-7 text-right font-medium ${
              effectiveValue ? 'text-neutral-900' : 'text-neutral-400'
            }`}
          >
            {effectiveValue ? 'On' : 'Off'}
          </T.Body>
          <Switch
            ariaLabel={`Toggle ${name}`}
            onValueChange={handleValueChange}
            value={effectiveValue}
          />
        </HStack>
        <T.BodySmall
          className={`whitespace-nowrap ${isDeviating ? 'text-orange-700' : 'text-neutral-400'}`}
        >
          {isDeviating ? `Statsig: ${statsigValue ? 'On' : 'Off'}` : 'Following Statsig'}
        </T.BodySmall>
      </VStack>
    </Flex>
  )
}
const MemoizedFeatureFlagRow = React.memo(FeatureFlagRow)

type FeatureFlagDevPanelModalProps = {
  onClose: () => void
}
const FeatureFlagDevPanelModal = ({ onClose }: FeatureFlagDevPanelModalProps) => {
  const overrides = useFeatureFlagOverrides()
  const checkStatsigGate = useCheckStatsigGate()
  const [search, setSearch] = useState('')

  const filteredEntries = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return FeatureGateEntries
    }
    return FeatureGateEntries.filter(
      ({ label, gate }) => label.toLowerCase().includes(query) || gate.toLowerCase().includes(query)
    )
  }, [search])

  const overrideCount = Object.keys(overrides).length

  return (
    <Modal onClose={onClose} width="640px">
      <Modal.Header>Feature Flags (Local Overrides)</Modal.Header>
      <Modal.Content>
        <VStack gap={4}>
          <T.BodySmall className="text-neutral-500">
            Each toggle shows the resolved value. Overrides are stored in this browser, take
            precedence over Statsig, and never apply in production. An{' '}
            <span className="font-medium text-orange-700">Overridden</span> gate is deviating from
            its Statsig default; toggle it back to that default to clear the override. Badges show
            where a gate is consumed — <span className="font-medium text-blue-700">Frontend</span>,{' '}
            <span className="font-medium text-green-700">Backend</span>, or{' '}
            <span className="font-medium text-purple-700">Both</span>; backend-only gates show this
            browser&apos;s Statsig read as their default.
          </T.BodySmall>
          <Input
            ariaLabel="Search feature flags"
            onValueChange={setSearch}
            placeholder="Search by name or gate key"
            prefixIcon={faMagnifyingGlass}
            value={search}
          />
          <VStack gap={3}>
            {filteredEntries.map(({ label, gate, scope }) => (
              <MemoizedFeatureFlagRow
                key={gate}
                gate={gate}
                name={label}
                override={overrides[gate]}
                scope={scope}
                statsigValue={checkStatsigGate(gate)}
              />
            ))}
            {filteredEntries.length === 0 && (
              <T.BodySmall className="text-neutral-500">No matching feature flags.</T.BodySmall>
            )}
          </VStack>
        </VStack>
      </Modal.Content>
      <Modal.Footer>
        <Button
          variant="default"
          color="danger"
          isDisabled={overrideCount === 0}
          onClick={clearFeatureFlagOverrides}
        >
          {overrideCount === 0 ? 'Reset all' : `Reset all (${overrideCount})`}
        </Button>
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

/**
 * Dev-only control for forcing feature gates on/off locally, toggled with
 * Cmd/Ctrl+Shift+F. Hidden in production; overrides persist across refreshes
 * via `featureFlagOverrides`.
 */
const FeatureFlagDevPanel = () => {
  const { isOpen, toggle, close } = useDisclosure()

  useEffect(() => {
    if (!areFeatureFlagOverridesEnabled) {
      return undefined
    }
    const listener = (e: KeyboardEvent) => {
      // e.key is uppercase when Shift is held, so compare case-insensitively.
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === KeyboardKey.F) {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [toggle])

  if (!areFeatureFlagOverridesEnabled || !isOpen) {
    return null
  }

  return <FeatureFlagDevPanelModal onClose={close} />
}

export default React.memo(FeatureFlagDevPanel)
